import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  APPLE_TEAM_TYPES,
  getUserErrorMessage,
  type MobileAppConfigCvx,
  mobileAppConfigStrictSchema,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { Info } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { FormWrapper } from '@/components/FormWrapper';
import { LoadingIcon } from '@/components/LoadingIcon';
import { NullableInput } from '@/components/NullableInput';
import { UploadButton } from '@/components/UploadButton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { NativePolicyForm, NativePolicyFormHeader } from '@/features/app-updates/NativePolicyForm';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';

const formValuesSchema = mobileAppConfigStrictSchema;
type FormValues = z.output<typeof formValuesSchema>;

/** The text fields before the Apple Team Type select. */
const APP_FIELDS = [
  { name: 'displayName', label: 'Display Name' },
  { name: 'iosBundleId', label: 'iOS Bundle ID' },
  { name: 'appleMerchantId', label: 'Apple Merchant ID' },
  { name: 'androidBundleId', label: 'Android Bundle ID' },
] as const;

/** The text fields after the Apple Team Type select. */
const APPLE_ACCOUNT_FIELDS = [
  { name: 'expoAppleTeamId', label: 'Apple Team ID' },
  { name: 'expoAppleAscIssuerId', label: 'Apple App Store Connect Issuer ID' },
  { name: 'expoAppleAscKeyId', label: 'Apple App Store Connect Key ID' },
  { name: 'expoAppleId', label: 'Apple ID' },
  { name: 'expoApplePassword', label: 'Apple ID Password (encrypted)' },
  { name: 'expoAppleAppSpecificPassword', label: 'Apple App Specific Password (encrypted)' },
] as const;

export const Route = createFileRoute('/r/$restaurantId/s/mobile-app-config')({
  component: MobileAppConfigPage,
});

function MobileAppConfigPage() {
  const { restaurantId } = Route.useParams();
  const {
    data: mobileAppConfig,
    error,
    isError,
    isPending,
  } = useQuery(api.mobileAppConfigs.getByRestaurantId, { restaurantId });

  if (isPending) return <LoadingIcon />;

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading mobile app config" />;
  }

  return <MobileAppConfigForm mobileAppConfig={mobileAppConfig} />;
}

type MobileAppConfigFormProps = {
  mobileAppConfig: MobileAppConfigCvx | null;
};

const MobileAppConfigForm = ({ mobileAppConfig }: MobileAppConfigFormProps) => {
  const { restaurantId } = Route.useParams();
  const convex = useConvex();

  const form = useForm({
    defaultValues: mobileAppConfig ?? { expoAppleTeamType: 'IN_HOUSE' },
    resolver: zodResolver(formValuesSchema),
  });

  // Subscribe to changes
  useEffect(() => {
    if (!mobileAppConfig) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(mobileAppConfig, { keepDirty: true, keepDirtyValues: true });
  }, [form, mobileAppConfig]);

  const onValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.mobileAppConfigs.save, {
        mobileAppConfig: formValues,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  const handleDeleteImage = async (type: 'icon' | 'banner') => {
    try {
      await convex.mutation(api.mobileAppConfigs.deleteImage, { restaurantId, type });
    } catch (error) {
      toast.error("Couldn't delete the image", { description: getUserErrorMessage(error) });
    }
  };

  const textField = ({
    name,
    label,
  }: (typeof APP_FIELDS)[number] | (typeof APPLE_ACCOUNT_FIELDS)[number]) => (
    <FormField
      control={form.control}
      key={name}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <NullableInput {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>Mobile App Config</CardTitle>
          <CardDescription>Build native iOS & Android consumer apps</CardDescription>
        </CardHeader>
        <CardContent>
          <FormWrapper {...form}>
            <form
              id="form1"
              onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
              className="grid gap-6"
            >
              {APP_FIELDS.map(textField)}

              {/* Apple Team Type  */}
              <FormField
                control={form.control}
                name="expoAppleTeamType"
                render={({ field }) => (
                  <FormItem className="w-[280px]">
                    <FormLabel>Apple Team Type</FormLabel>
                    <Select defaultValue={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {APPLE_TEAM_TYPES.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {APPLE_ACCOUNT_FIELDS.map(textField)}

              {/* Images (can only be added once initial config is set up) */}
              {!!mobileAppConfig?._id && (
                <div className={'flex gap-4 justify-end'}>
                  {!mobileAppConfig.iconImageStorageId ? (
                    <UploadButton
                      buttonText="Upload icon"
                      fileTypes={['.png']}
                      imageHeight={1024}
                      imageWidth={1024}
                      onUploadComplete={async (storageId) => {
                        await convex.mutation(api.mobileAppConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'icon',
                        });
                        toast.success('Icon uploaded successfully');
                      }}
                    />
                  ) : (
                    <div className="flex gap-2">
                      {mobileAppConfig.iconImageStorageUrl && (
                        <ActionButton
                          onClick={(e) => {
                            e.preventDefault();
                            if (mobileAppConfig.iconImageStorageUrl) {
                              window.open(mobileAppConfig.iconImageStorageUrl, '_blank');
                            }
                          }}
                          variant={'outline'}
                        >
                          View icon
                        </ActionButton>
                      )}
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await handleDeleteImage('icon');
                        }}
                        variant={'destructive'}
                      >
                        Delete icon
                      </ActionButton>
                    </div>
                  )}

                  {!mobileAppConfig.bannerImageStorageId ? (
                    <UploadButton
                      buttonText="Upload banner image"
                      fileTypes={['.jpg']}
                      imageHeight={560}
                      imageWidth={750}
                      onUploadComplete={async (storageId) => {
                        await convex.mutation(api.mobileAppConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'banner',
                        });
                        toast.success('Banner uploaded successfully');
                      }}
                    />
                  ) : (
                    <div className="flex gap-2">
                      {mobileAppConfig.bannerImageStorageUrl && (
                        <ActionButton
                          onClick={(e) => {
                            e.preventDefault();
                            if (mobileAppConfig.bannerImageStorageUrl) {
                              window.open(mobileAppConfig.bannerImageStorageUrl, '_blank');
                            }
                          }}
                          variant={'outline'}
                        >
                          View banner
                        </ActionButton>
                      )}
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await handleDeleteImage('banner');
                        }}
                        variant={'destructive'}
                      >
                        Delete banner
                      </ActionButton>
                    </div>
                  )}
                </div>
              )}
            </form>
          </FormWrapper>
        </CardContent>
        <CardFooter className="border-t">
          <ActionButton
            disabled={form.formState.isSubmitting || !form.formState.isDirty}
            loading={form.formState.isSubmitting}
            type="submit"
            form="form1"
          >
            Save
          </ActionButton>
        </CardFooter>
      </Card>

      {mobileAppConfig && <StoreUpdatesCard mobileAppConfig={mobileAppConfig} />}
    </div>
  );
};

/** The brand's two store listings, keyed by the config's bundle IDs. */
const StoreUpdatesCard = ({ mobileAppConfig }: { mobileAppConfig: MobileAppConfigCvx }) => {
  const { data, error, isError, isPending } = useQuery(api.appUpdates.getAll);
  const listings = [
    { bundleId: mobileAppConfig.iosBundleId, platform: 'ios' },
    { bundleId: mobileAppConfig.androidBundleId, platform: 'android' },
  ] as const;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="mb-2">App Store Updates</CardTitle>
        <Alert className="bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
          <Info className="h-4 w-4" />
          <AlertTitle>Latest version and force update</AlertTitle>
          <AlertDescription>
            Keep these in sync with the App Store and Google Play. Below the latest version, the app
            offers the update. With force update on, it's blocked until it updates.
          </AlertDescription>
        </Alert>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isError ? (
          <ErrorAlert message={error.message} title="Error loading store updates" />
        ) : isPending ? (
          <LoadingIcon />
        ) : (
          <>
            <NativePolicyFormHeader />
            {listings.map(({ bundleId, platform }) => (
              <NativePolicyForm
                key={platform}
                bundleId={bundleId}
                platform={platform}
                nativePolicies={data.nativePolicies}
              />
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};
